import React, { useState, useRef, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Typography,
  SxProps,
  Stack,
  Paper,
  Avatar,
  ListItem,
} from '@mui/material';
import { useDictionary, useIsDesktop } from 'Hooks';
import { PurpleSearchIcon, PurpleXLite } from 'Images';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';

interface IOption {
  label: string;
  value: string;
  group?: string;
}

interface ICustomNewAutoComplete {
  value?: string;
  setValue: (value: string | null) => void;
  options: IOption[];
  recentOptions: IOption[];
  label: string;
  placeholder?: string;
  style?: SxProps;
  paperComponent?: React.ReactNode;
  groupName?: string;
  handleSubCategoryClick?: (id: number) => void;
  getRequestId?: (name: string) => number | undefined;
}

export const CustomNewAutoComplete: React.FC<ICustomNewAutoComplete> = ({
  value,
  setValue,
  options,
  recentOptions,
  label,
  placeholder,
  style,
  paperComponent,
  groupName,
}) => {
  const isDesktop = useIsDesktop();
  const [isOpen, setOpen] = useState(false);
  const autoC = useRef<HTMLDivElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);
  const dictionary = useDictionary(Dictionary, DICTIONARY.Reimbursements);

  const allOptions: IOption[] = [
    ...recentOptions.map((option) => ({
      ...option,
      group: groupName,
    })),
    ...options.map((option) => ({
      ...option,
      group: '',
    })),
  ];

  const lastOption = allOptions[allOptions.length - 1];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popperRef.current &&
        !popperRef.current.contains(event.target as Node) &&
        !autoC.current?.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (paperComponent) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [paperComponent]);

  const CustomPaper: React.FC<React.ComponentProps<typeof Paper>> = (props) => {
    return (
      <Paper
        {...props}
        sx={{
          padding: '14px',
          boxShadow: 'none',
          bgcolor: '#fff',
          color: 'text.secondary',
          fontWeight: 400,
          borderRadius: '0 0 16px 16px',
          outline: '0px',
          border: '1px solid',
          borderColor: 'primary.main',
          borderTop: '0px',
          '& ul': {
            bgcolor: '#fff',
            position: 'relative',
            p: '0px',
          },
          '& .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused': {
            borderRadius: '12px',
            border: '1px solid #5B86EF',
            bgcolor: 'primary.shade_100',
          },
        }}
      />
    );
  };

  return (
    <Autocomplete
      ref={autoC}
      disablePortal
      open={isOpen}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value ? allOptions.find((option) => option.value === value) : null}
      onChange={(_, newValue: IOption | null) =>
        setValue(newValue ? newValue.value : null)
      }
      options={allOptions}
      groupBy={(option) => option?.group || ''}
      getOptionLabel={(option) => option?.label || ''}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: false,
            sx: {
              display: value ? 'none' : undefined,
              '&.Mui-focused': {
                display: 'none',
              },
              mt: '-4px',
            },
          }}
          inputProps={{ ...params.inputProps, 'aria-label': label }}
        />
      )}
      noOptionsText={
        <Typography role='alert'>{dictionary.noOptions}</Typography>
      }
      renderGroup={(params) => (
        <Stack key={params.key} mb='10px'>
          {params.group !== '' && (
            <Typography
              variant={isDesktop ? 'body2' : 'body5'}
              sx={{
                fontWeight: 'bold',
                mt: 2,
                display: 'flex',
                justifyContent: 'start',
              }}
            >
              {params.group}
            </Typography>
          )}
          {params.children}
        </Stack>
      )}
      PaperComponent={CustomPaper}
      clearIcon={
        <Avatar
          alt=''
          src={PurpleXLite}
          sx={{
            width: '20px',
            height: '20px',
          }}
        />
      }
      renderOption={(props, option, { selected }) => (
        <ListItem
          {...props}
          sx={{
            fontWeight: option === lastOption ? 'bold' : 'normal',
            paddingRight: option.group !== '' ? '0px' : undefined,
          }}
        >
          <Typography variant={'body3'}>{option?.label || ''}</Typography>
        </ListItem>
      )}
      popupIcon={
        <Avatar
          alt=''
          src={PurpleSearchIcon}
          sx={{
            width: isDesktop ? '40px' : '32px',
            height: isDesktop ? '40px' : '32px',
            mt: isOpen ? '0px' : '-8px',
            mb: !isOpen ? '0px' : '-8px',
            transform: isOpen ? 'rotate(180deg)' : undefined,
          }}
        />
      }
      sx={{
        width: '100%',
        maxWidth: isDesktop ? '450px' : '100%',
        m: isDesktop ? undefined : '0 auto',
        borderRadius: isOpen ? '16px 16px 0 0' : '56px',
        bgcolor: '#fff',

        '&&&:has(input[dropdown="open"])': {
          borderRadius: '16px 16px 0 0',
        },
        '&&& .MuiOutlinedInput-notchedOutline': {
          borderRadius: isOpen ? '16px 16px 0 0' : '56px',
          borderColor: isOpen ? 'primary.main' : 'primary.shade_200',
          borderWidth: '1px',
          borderBottom: isOpen ? '0' : '',
        },
        '&&& .MuiInputBase-root input[dropdown="open"] ~ fieldset': {
          borderColor: 'primary.main',
          borderRadius: '16px 16px 0 0',
          borderBottom: '0',
          borderWidth: '1px',
        },
        '&&& .MuiInputBase-root input[dropdown="closed"] ~ fieldset': {
          border: '1px solid',
          borderColor: 'primary.shade_200',
        },

        '&&& .MuiInputLabel-root': {
          p: isDesktop ? '2px 0 0 8px' : 0,
        },
        '&&& .MuiInputLabel-shrink': {
          p: 0,
        },
        '&&& .MuiOutlinedInput-root': {
          height: isDesktop ? '56px' : '42px',
          bgcolor: 'white',
        },
        '&&& .MuiOutlinedInput-input': {
          p: '0 15px',
        },
        ...style,
      }}
    />
  );
};

export default CustomNewAutoComplete;
